/**
 * Sandbox System Types
 * For secure command execution
 */

export interface SandboxConfig {
  /** Whether sandbox is enabled */
  enabled: boolean;

  /** Allow network access */
  allowNetwork: boolean;

  /** Allowed paths (read/write) */
  allowedPaths: string[];

  /** Read-only paths */
  readOnlyPaths: string[];

  /** Explicitly denied paths */
  deniedPaths: string[];

  /** Command timeout in milliseconds */
  timeout: number;

  /** Maximum memory in MB */
  maxMemoryMB?: number;

  /** Maximum CPU time in seconds */
  maxCpuSeconds?: number;

  /** Allow access to environment variables */
  allowEnv: boolean;

  /** Specific environment variables to allow */
  allowedEnvVars?: string[];

  /** Sandbox method to use */
  method?: 'bubblewrap' | 'firejail' | 'sandbox-exec' | 'docker' | 'none';
}

export interface SandboxResult {
  /** Exit code */
  exitCode: number;

  /** Standard output */
  stdout: string;

  /** Standard error */
  stderr: string;

  /** Whether execution was killed (timeout, etc) */
  killed: boolean;

  /** Kill signal if killed */
  signal?: string;

  /** Execution duration in ms */
  duration_ms: number;

  /** Whether sandbox was used */
  sandboxed: boolean;

  /** Sandbox method used */
  method?: string;

  /** Error if execution failed */
  error?: string;
}

export interface SandboxCapabilities {
  /** Sandbox available on this system */
  available: boolean;

  /** Available sandbox methods */
  methods: string[];

  /** Recommended method */
  recommended?: string;

  /** Reason if not available */
  reason?: string;
}

export const DEFAULT_SANDBOX_CONFIG: SandboxConfig = {
  enabled: true,
  allowNetwork: false,
  allowedPaths: [],
  readOnlyPaths: ['/usr', '/lib', '/lib64', '/bin', '/sbin', '/etc'],
  deniedPaths: ['/etc/passwd', '/etc/shadow', '~/.ssh', '~/.gnupg'],
  timeout: 120000, // 2 minutes
  maxMemoryMB: 512,
  maxCpuSeconds: 60,
  allowEnv: true,
  allowedEnvVars: ['PATH', 'HOME', 'USER', 'SHELL', 'TERM', 'LANG', 'LC_ALL'],
};

// Commands that are safe to run without sandbox
export const SAFE_COMMANDS = [
  'ls', 'cat', 'head', 'tail', 'grep', 'find', 'which', 'pwd', 'echo',
  'date', 'whoami', 'hostname', 'uname', 'env', 'printenv',
  'git status', 'git log', 'git diff', 'git branch', 'git remote',
  'npm list', 'npm outdated', 'npm view',
  'node --version', 'npm --version', 'python --version', 'python3 --version',
];

// Commands that should always be sandboxed
export const DANGEROUS_COMMANDS = [
  'rm -rf', 'rm -r', 'rmdir',
  'chmod', 'chown', 'chgrp',
  'sudo', 'su',
  'curl', 'wget', 'nc', 'netcat',
  'ssh', 'scp', 'rsync',
  'dd', 'mkfs', 'fdisk', 'parted',
  'iptables', 'firewall',
  'systemctl', 'service',
  'kill', 'killall', 'pkill',
  'crontab', 'at',
];
